import { type UseInfiniteQueryResult, useInfiniteQuery } from "react-query"

import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import type { PaginatedPredictionReviewList } from "../../../types/generated/api/PaginatedPredictionReviewList"
import type { StandardErrorType } from "../../../utils/ErrorHandling"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"

/**
 * Custom hook for fetching prediction review history for a specific prediction.
 * Uses infinite query to load paginated review history records.
 *
 * @param predictionUuid - The UUID of the prediction to fetch review history for
 * @param enabled - Whether the query should be enabled
 * @returns Infinite query result containing paginated prediction review history
 */
export function usePredictionReviewHistory(
  predictionUuid: string,
  enabled: boolean = true,
): UseInfiniteQueryResult<AxiosResponse<PaginatedPredictionReviewList>, AxiosError<StandardErrorType>> {
  return useInfiniteQuery<AxiosResponse<PaginatedPredictionReviewList>, AxiosError<StandardErrorType>>(
    ["review-history", predictionUuid],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchPredictionReviewHistory({
        prediction_uuid: predictionUuid,
        page: pageParam,
        pageSize: 5,
      }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data.next) : false,
      enabled,
    },
  )
}
